import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { WaiterService } from '../../_service/waiter.service';
import Swal from 'sweetalert2';
import { FormControl, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { PusherService } from '../../_service/pusher.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { BuzzService, Buzz } from '../../_service/buzz.service';
import { OrderNotificationService } from '../../_service/order-notification.service';
import { ChatNotificationService } from '../../_service/chat-notification.service';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [NgbModule, RouterModule, ReactiveFormsModule, FormsModule, NgSelectModule, CommonModule],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss'
})
export class OrdersComponent implements OnInit, OnDestroy {
  // Loading states
  loading = false;
  unclaimedLoading = false;
  activeLoading = false;
  completedLoading = false;
  barcodesLoading = false;
  refreshing = false;

  unclaimedOrders: any;
  activeOrders: any;
  completedOrders: any;
  barcodeList: any;
  minutes = 0;

  orderForm = new FormGroup({
    barcode_id: new FormControl('', Validators.required)
  })
  tempItem: any;

  selectedDate: string = 'all';
  selectedTable: string = 'all';
  selectedStatus: string = 'all';

  selectedDateFilter: string = 'all';
  selectedTableFilter: string = 'all';
  selectedStatusFilter: string = 'all';

  // Store the original unfiltered data
  private _unclaimedOrdersData: any[] = [];
  private _activeOrdersData: any[] = [];
  private _completedOrdersData: any[] = [];

  private refreshSubscription: Subscription = new Subscription();

  newOrderCount: number = 0;
  hasNewOrders: boolean = false;
  private orderCountSubscription?: Subscription;

  // Add new properties for buzzes
  activeBuzzes: Buzz[] = [];
  private buzzSubscription?: Subscription;

  chatNotifications: string[] = [];
  private chatSubscription?: Subscription;

  searchTerm: string = '';
  filteredBarcodeList: any[] = [];

  constructor(
    private modal: NgbModal,
    private router: Router,
    private service: WaiterService,
    private buzzService: BuzzService,
    private orderNotificationService: OrderNotificationService,
    private chatNotificationService: ChatNotificationService
  ) { }

  ngOnInit(): void {
    this.loading = true; // Initial loading
    // Subscribe to order count changes
    this.initializeFlutterBridge();
    this.orderCountSubscription = this.orderNotificationService.getCount()
      .subscribe(count => {
        this.newOrderCount = count;
        this.hasNewOrders = count > 0;
        this.listUnclaimedOrders(); // Background refresh
      });

    this.loadInitialData();

    // Subscribe to buzz service
    this.buzzSubscription = this.buzzService.getBuzzes().subscribe(buzzes => {
      this.activeBuzzes = buzzes;
    });

    // Subscribe to chat notifications
    this.chatSubscription = this.chatNotificationService.getChatNotifications()
      .subscribe(notifications => {
        this.chatNotifications = notifications;
      });
  }

  // Load all initial data
  private loadInitialData() {
    const loadPromises = [
      this.listUnclaimedOrders(),
      this.listActiveOrders(),
      this.listCompletedOrders(),
      this.listBarcodes()
    ];

    Promise.all(loadPromises).finally(() => {
      this.loading = false;
    });
  }

  private initializeFlutterBridge() {
    // Define the interface for communicating with Flutter
    window.FlutterBridge = {
      postMessage: (message: string) => {
        // This will be overridden by Flutter's JavaScript channel
        console.log('Flutter bridge not initialized:', message);
      }
    };
  }
   printOrder(order: any) {
    // Prepare print data
    const printData = {
      type: 'print_order',
      data: {
        uid: order.uid,
        amount: order.total_amount,
        order_items: order.order_items,
        table_name: order.barcode_details?.name || '',
        transaction_date: order.created_at,
        status: order.status,
        // Include any additional data needed for printing
        payment_details: order.payment_details,
        deductions: order.deductions
      }
    };

    // Try to send through Flutter bridge
    try {
      window.FlutterBridge.postMessage(JSON.stringify(printData));
    } catch (e) {
      console.error('Failed to communicate with Flutter:', e);
      // Show fallback error message
      Swal.fire({
        title: 'Print Error',
        text: 'Unable to connect to printer. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }

  ngOnDestroy(): void {
    if (this.orderCountSubscription) {
      this.orderCountSubscription.unsubscribe();
    }
    if (this.buzzSubscription) {
      this.buzzSubscription.unsubscribe();
    }
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
    }
  }

  resetNewOrderNotification() {
    this.orderNotificationService.resetCount();
    this.listUnclaimedOrders(); // Refresh the unclaimed orders list
  }

  playBeep() {
    const audio = new Audio('beep.mp3'); // Replace with the correct path to your audio file
    audio.load(); // Load the audio file before playing it
    audio.play().catch(error => {
      console.error("Error playing audio", error); // Catch errors if any
    });
  }
  listUnclaimedOrders(): Promise<void> {
    this.unclaimedLoading = true;
    return new Promise((resolve, reject) => {
      this.service.viewUnclaimedOrder().subscribe({
        next: (res: any) => {
          this._unclaimedOrdersData = res as any[];
          this.applyFilters();
          resolve();
        },
        error: (err) => {
          console.error('Error loading unclaimed orders:', err);
          reject(err);
        },
        complete: () => {
          this.unclaimedLoading = false;
        }
      });
    });
  }
  listActiveOrders(): Promise<void> {
    this.activeLoading = true;
    return new Promise((resolve, reject) => {
      this.service.listActiveOrders().subscribe({
        next: (res: any) => {
          this._activeOrdersData = res.data;
          this.applyFilters();
          resolve();
        },
        error: (err) => {
          console.error('Error loading active orders:', err);
          reject(err);
        },
        complete: () => {
          this.activeLoading = false;
        }
      });
    });
  }
  listCompletedOrders(): Promise<void> {
    this.completedLoading = true;
    return new Promise((resolve, reject) => {
      this.service.listCompletedOrders().subscribe({
        next: (res: any) => {
          this._completedOrdersData = res.data;
          this.applyFilters();
          resolve();
        },
        error: (err) => {
          console.error('Error loading completed orders:', err);
          reject(err);
        },
        complete: () => {
          this.completedLoading = false;
        }
      });
    });
  }
  AcceptItemModal(item: any, content: any) {
    this.tempItem = item;
    this.modal.dismissAll();
    this.modal.open(content, { centered: true, size: 'md' }).result;
  }
  minus() {
    if (this.minutes >= 0.1) {
      this.minutes--
    }
  }
  plus() {
    this.minutes++
  }

  // Set minutes from preset buttons
  setMinutes(value: number) {
    this.minutes = value;
  }

  // Get estimated completion time
  getEstimatedTime(): string {
    const now = new Date();
    const estimated = new Date(now.getTime() + (this.minutes * 60000));
    return estimated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  Accept(item: any) {
    let payload = {
      delay: this.minutes,
    }
    if (this.minutes == 0) {
      return;
    }
    this.service.claimOrder(item.id, payload).subscribe({
      next: (res) => {
      }, error: (err) => {
        Swal.fire('Failed', err.error.message, 'error')
      },
      complete: () => {
        this.modal.dismissAll();
        this.router.navigateByUrl(`/orders/${item.uid}`)
      }
    })
  }

  createOrder(content: any) {
    this.modal.open(content, { centered: true, size: 'md' }).result
  }
  scan() {
    this.modal.dismissAll()
    this.router.navigateByUrl('/create-order')
  }
  listBarcodes(): Promise<void> {
    this.barcodesLoading = true;
    return new Promise((resolve, reject) => {
      this.service.listBarcodes().subscribe({
        next: (res: any) => {
          this.barcodeList = res;
          this.filteredBarcodeList = res; // Initialize filtered list
          resolve();
        },
        error: (err) => {
          console.error('Error loading barcodes:', err);
          reject(err);
        },
        complete: () => {
          this.barcodesLoading = false;
        }
      });
    });
  }

  start() {
    const data = this.orderForm.value
    // let orderDetails = {}
    // orderDetails.push(data)
    localStorage.removeItem('cart');
    localStorage.setItem('orders', JSON.stringify(data))
    this.modal.dismissAll()
    this.router.navigateByUrl('/create-order')
  }

  // New method to apply filters
  private applyFilters() {
    // Filter unclaimed orders
    this.unclaimedOrders = this._unclaimedOrdersData.filter(order => {
      return this.matchesFilters(order, 'unclaimed');
    });

    // Filter active orders
    this.activeOrders = this._activeOrdersData.filter(order => {
      return this.matchesFilters(order, 'active');
    });

    // Filter completed orders
    this.completedOrders = this._completedOrdersData.filter(order => {
      return this.matchesFilters(order, 'completed');
    });
  }

  private matchesFilters(order: any, orderType: 'unclaimed' | 'active' | 'completed'): boolean {
    let matches = true;

    // Date filter
    if (this.selectedDateFilter !== 'all') {
      const orderDate = new Date(order.created_at);
      const today = new Date();

      switch (this.selectedDateFilter) {
        case 'today':
          matches = matches && this.isToday(orderDate);
          break;
        case 'week':
          matches = matches && this.isThisWeek(orderDate);
          break;
        case 'month':
          matches = matches && this.isThisMonth(orderDate);
          break;
      }
    }

    // Table filter
    if (this.selectedTableFilter !== 'all') {
      matches = matches && order.barcode_id === this.selectedTableFilter;
    }

    // Status filter (only for active orders)
    if (orderType === 'active' && this.selectedStatusFilter !== 'all') {
      matches = matches && order.status === this.selectedStatusFilter;
    }

    return matches;
  }

  // Helper methods for date filtering
  private isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  private isThisWeek(date: Date): boolean {
    const today = new Date();
    const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
    const weekEnd = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    return date >= weekStart && date <= weekEnd;
  }

  private isThisMonth(date: Date): boolean {
    const today = new Date();
    return date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  // Update filter methods to use frontend filtering
  onDateFilter(date: string) {
    this.selectedDateFilter = date;
    this.applyFilters();
  }

  onTableFilter(tableId: string) {
    this.selectedTableFilter = tableId;
    this.applyFilters();
  }

  onStatusFilter(status: string) {
    this.selectedStatusFilter = status;
    this.applyFilters();
  }

  clearFilters() {
    this.selectedDateFilter = 'all';
    this.selectedTableFilter = 'all';
    this.selectedStatusFilter = 'all';
    this.applyFilters();
  }

  getSelectedDateLabel(): string {
    switch(this.selectedDateFilter) {
      case 'today': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'custom': return 'Custom Date';
      case 'all': return 'Date';
      default: return 'Date';
    }
  }

  getSelectedTableLabel(): string {
    if (this.selectedTableFilter !== 'all') {
      const selectedTable = this.barcodeList.find((t: { id: string }) => t.id === this.selectedTableFilter);
      return selectedTable ? selectedTable.name : 'Table';
    }
    return 'Table';
  }

  getSelectedStatusLabel(): string {
    switch(this.selectedStatusFilter) {
      case 'delivered': return 'Order-fulfilled';
      case 'accepted': return 'Order In Progress';
      case 'all': return 'Status';
      default: return 'Status';
    }
  }

  getStatusClass(status: string): string {
    switch(status) {
      case 'delivered':
        return 'text-success'; // green for fulfilled
      case 'accepted':
        return 'text-warning'; // yellow/orange for in progress
      default:
        return '';
    }
  }

  getStatusIcon(status: string): string {
    switch(status) {
      case 'delivered':
        return 'fa-check-circle';
      case 'accepted':
        return 'fa-clock';
      default:
        return 'fa-filter';
    }
  }

  refreshData() {
    this.refreshing = true;
    // Refresh all lists
    const refreshPromises = [
      this.listUnclaimedOrders(),
      this.listCompletedOrders(),
      this.listActiveOrders(),
      this.listBarcodes()
    ];

    Promise.all(refreshPromises).finally(() => {
      setTimeout(() => {
        this.refreshing = false;
      }, 1000); // Show refresh indicator for at least 1 second
    });
  }

  clearBuzz(index: number) {
    this.buzzService.clearBuzz(index);
  }

  // Helper method to format time elapsed
  getTimeElapsed(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    return `${minutes} minutes ago`;
  }

  hasChatNotification(orderId: string): boolean {
    return this.chatNotifications.includes(orderId);
  }

  filterBarcodes(event: any) {
    const searchTerm = event.target.value.toLowerCase();
    this.filteredBarcodeList = this.barcodeList.filter((barcode: any) =>
      barcode.name.toLowerCase().includes(searchTerm) ||
      barcode.id.toString().includes(searchTerm)
    );
  }

  // TrackBy function for ngFor optimization
  trackByOrderId(index: number, item: any): any {
    return item.id || index;
  }
}
declare global {
  interface Window {
    FlutterBridge: {
      postMessage(message: string): void;
    };
    FlutterChannel?: {
      postMessage(message: string): void;
    };
  }
}
